import React, { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import api, { chatbotsAPI, knowledgeAPI, moderatorsAPI, calendarAPI } from '@/services/api';
import logger from '@/utils/logger';
import { cn } from '@/lib/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { 
  Bot, 
  Settings, 
  Database, 
  Code,
  FileText,
  Upload,
  Plus,
  Trash2,
  Loader2,
  ArrowLeft,
  Copy,
  Check,
  Globe,
  MessageSquare,
  Palette,
  BarChart3,
  Send,
  RefreshCw,
  Calendar,
  Clock,
  Users,
  User,
  Mail,
  UserPlus,
  Share2,
  MessageCircle,
  ExternalLink,
  Link2,
  CheckCircle,
  AlertCircle,
  Info,
  HelpCircle,
  Camera,
  Webhook,
  QrCode,
  Download,
  Zap,
  History,
  Headphones,
  Sparkles,
  Phone,
  Heart,
  Star,
  RotateCcw
} from 'lucide-react';
import { HelpButton } from '@/components/common/HelpButton';
import { toast } from 'sonner';

import { Switch } from '@/components/ui/switch';
import { WebhooksTab } from '@/components/chatbot';
import { EmbedTab } from '@/components/chatbot/EmbedTab';
import { ThemeTab } from '@/components/chatbot/ThemeTab';
import { SettingsTab } from '@/components/chatbot/SettingsTab';
import { KnowledgeTab } from '@/components/chatbot/KnowledgeTab';
import {
  AddTextDocumentDialog,
  AddAPISourceDialog,
  SitemapImportDialog,
  ViewDocumentDialog,
  AddModeratorDialog,
  DeleteConfirmDialog,
} from '@/components/chatbot/dialogs';
import { ChannelsTab } from '@/components/chatbot/ChannelsTab';
import { InteractionsTab } from '@/components/chatbot/InteractionsTab';
import { SupportPortalsTab } from '@/components/chatbot/SupportPortalsTab';
import { RulesTab } from '@/components/chatbot/RulesTab';
import { ModeratorsTab } from '@/components/chatbot/ModeratorsTab';
import { AgentHistoryTab } from '@/components/chatbot/AgentHistoryTab';
import { useAuth } from '@/context/AuthContext';
import { PERSONA_TAGS } from '@/data/personas';
const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ChatbotDetailPage = () => {
  const { chatbotId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);
  const { featureAccess, user } = useAuth();
  const isModeratorOnly = !!user?.is_moderator_only;
  // iter128: moderators now share the same tab layout as owners, EXCEPT the
  // "Moderators" management tab (they can't invite others). Default tab is
  // 'settings' for everyone so the agent detail page lands on the primary
  // configuration screen after selection.
  const defaultTab = new URLSearchParams(window.location.search).get('tab') || 'settings';
  
  // Check if social channels are allowed by the plan
  const socialChannelsEnabled = featureAccess?.social_channels !== false;
  
  const [chatbot, setChatbot] = useState(location.state?.chatbot || null);
  const [documents, setDocuments] = useState([]);
  const [refreshingKnowledge, setRefreshingKnowledge] = useState(false);
  const [apiSources, setApiSources] = useState([]);
  const [loading, setLoading] = useState(!location.state?.chatbot);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [copied, setCopied] = useState(false);
  
  // Test chat state
  const [testChatOpen, setTestChatOpen] = useState(false);
  const [testMessages, setTestMessages] = useState([]);
  const [testInput, setTestInput] = useState('');
  const [testSessionId, setTestSessionId] = useState(null);
  const [testLoading, setTestLoading] = useState(false);
  
  // Track if form has unsaved changes
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [initialFormData, setInitialFormData] = useState(null);
  const [initialTheme, setInitialTheme] = useState(null);
  const [initialRules, setInitialRules] = useState(null);
  const [initialBookingSettings, setInitialBookingSettings] = useState(null);
  
  // URL and document view dialogs
  const [showUrlDialog, setShowUrlDialog] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const [urlName, setUrlName] = useState('');
  const [addingUrl, setAddingUrl] = useState(false);
  const [showDocViewDialog, setShowDocViewDialog] = useState(false);
  const [viewingDocument, setViewingDocument] = useState(null);

  // iter132.2 — proper delete confirmation dialog (previously the delete
  // fired immediately with no confirmation, which cost customers real
  // data on mis-clicks). Populated by the KnowledgeTab trash icon and
  // by the ViewDocumentDialog Delete button.
  const [docToDelete, setDocToDelete] = useState(null);
  const [deletingDoc, setDeletingDoc] = useState(false);
  
  // Sitemap import dialog
  const [showSitemapDialog, setShowSitemapDialog] = useState(false);
  const [sitemapUrl, setSitemapUrl] = useState('');
  const [maxPages, setMaxPages] = useState(20);
  const [deepCrawl, setDeepCrawl] = useState(false);
  const [importingSitemap, setImportingSitemap] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    welcome_message: '',
    system_prompt: '',
    response_tone: 'balanced',
    enable_followup_questions: false,
    status: 'draft',
    icon: 'Bot',
    icon_color: 'blue',
    avatar_url: ''
  });
  
  // Rules state
  const [rules, setRules] = useState([]);
  const [newRuleText, setNewRuleText] = useState('');
  const [editingRule, setEditingRule] = useState(null);
  
  // Theme state
  const [theme, setTheme] = useState({
    primary_color: '#3b82f6',
    secondary_color: '#f1f5f9',
    text_color: '#1f2937',
    background_color: '#ffffff',
    border_radius: 16,
    position: 'bottom-right',
    widget_style: 'floating',
    widget_button_icon: 'chat',
    widget_button_text: '',
    widget_icon_url: '',
  });
  
  // Dialog states
  const [showTextDocDialog, setShowTextDocDialog] = useState(false);
  const [showAPISourceDialog, setShowAPISourceDialog] = useState(false);
  const [showAPISourceHelp, setShowAPISourceHelp] = useState(false);
  const [newTextDoc, setNewTextDoc] = useState({ name: '', content: '' });
  const [newAPISource, setNewAPISource] = useState({ 
    name: '', 
    endpoint_url: '', 
    method: 'GET',
    refresh_interval_minutes: 60 
  });
  
  // Booking settings state
  const [bookingSettings, setBookingSettings] = useState({
    enabled: false,
    calendar_connection_id: '',
    slot_duration_minutes: 30,
    buffer_minutes: 15,
    advance_days: 14,
    working_hours_start: '09:00',
    working_hours_end: '17:00',
    working_days: [0, 1, 2, 3, 4], // Mon-Fri
    timezone: 'UTC',
    confirmation_message: 'Your appointment has been booked! You\'ll receive a calendar invite shortly.'
  });
  const [calendarConnections, setCalendarConnections] = useState([]);
  const [syncingCalendar, setSyncingCalendar] = useState(false);
  const [vacationBlocks, setVacationBlocks] = useState([]);
  const [loadingConnections, setLoadingConnections] = useState(false);
  
  // Moderators state
  const [moderators, setModerators] = useState([]);
  const [loadingModerators, setLoadingModerators] = useState(false);
  const [showAddModeratorDialog, setShowAddModeratorDialog] = useState(false);
  const [newModeratorEmail, setNewModeratorEmail] = useState('');
  const [addingModerator, setAddingModerator] = useState(false);
  
  // Social Media Channels state
  const [channels, setChannels] = useState({
    whatsapp: { connected: false, phone: '', business_id: '', api_key: '' },
    facebook: { connected: false, page_id: '', page_name: '', access_token: '' },
    instagram: { connected: false, account_id: '', username: '', access_token: '' },
    linkedin: { connected: false, company_id: '', company_name: '', access_token: '' }
  });
  const [savingChannel, setSavingChannel] = useState(null);

  useEffect(() => {
    if (!chatbot) {
      loadChatbot();
    } else {
      initializeForm(chatbot);
      loadKnowledgeBase();
      loadCalendarConnections();
      loadModerators();
      loadChannels();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatbotId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [testMessages]);

  const initializeForm = (bot) => {
    const newFormData = {
      name: bot.name || '',
      description: bot.description || '',
      welcome_message: bot.welcome_message || '',
      system_prompt: bot.system_prompt || '',
      response_tone: bot.response_tone || 'balanced',
      enable_followup_questions: bot.enable_followup_questions || false,
      status: bot.status || 'draft',
      icon: bot.icon || 'Bot',
      icon_color: bot.icon_color || 'blue',
      avatar_url: bot.avatar_url || ''
    };
    setFormData(newFormData);
    setInitialFormData(JSON.stringify(newFormData));
    setHasUnsavedChanges(false);
    
    const newTheme = {
      primary_color: bot.widget_theme?.primary_color || '#3b82f6',
      secondary_color: bot.widget_theme?.secondary_color || '#f1f5f9',
      text_color: bot.widget_theme?.text_color || '#1f2937',
      background_color: bot.widget_theme?.background_color || '#ffffff',
      border_radius: bot.widget_theme?.border_radius || 16,
      position: bot.widget_theme?.position || 'bottom-right',
      widget_style: bot.widget_theme?.widget_style || 'floating',
      widget_button_icon: bot.widget_theme?.widget_button_icon || 'chat',
      widget_button_text: bot.widget_theme?.widget_button_text || '',
      widget_icon_url: bot.widget_theme?.widget_icon_url || '',
    };
    setTheme(newTheme);
    setInitialTheme(JSON.stringify(newTheme));
    
    // Load rules
    const newRules = bot.rules || [];
    setRules(newRules);
    setInitialRules(JSON.stringify(newRules));
    
    // Load booking settings
    const newBookingSettings = {
      enabled: bot.booking_settings?.enabled || false,
      calendar_connection_id: bot.booking_settings?.calendar_connection_id || '',
      slot_duration_minutes: bot.booking_settings?.slot_duration_minutes || 30,
      buffer_minutes: bot.booking_settings?.buffer_minutes || 15,
      advance_days: bot.booking_settings?.advance_days || 14,
      working_hours_start: bot.booking_settings?.working_hours_start || '09:00',
      working_hours_end: bot.booking_settings?.working_hours_end || '17:00',
      working_days: bot.booking_settings?.working_days || [0, 1, 2, 3, 4],
      timezone: bot.booking_settings?.timezone || 'UTC',
      confirmation_message: bot.booking_settings?.confirmation_message || 'Your appointment has been booked! You\'ll receive a calendar invite shortly.'
    };
    setBookingSettings(newBookingSettings);
    setInitialBookingSettings(JSON.stringify(newBookingSettings));
  };

  // Track form changes - compare all editable states
  useEffect(() => {
    if (initialFormData !== null) {
      const formChanged = JSON.stringify(formData) !== initialFormData;
      const themeChanged = initialTheme !== null && JSON.stringify(theme) !== initialTheme;
      const rulesChanged = initialRules !== null && JSON.stringify(rules) !== initialRules;
      const bookingChanged = initialBookingSettings !== null && JSON.stringify(bookingSettings) !== initialBookingSettings;
      
      setHasUnsavedChanges(formChanged || themeChanged || rulesChanged || bookingChanged);
    }
  }, [formData, theme, rules, bookingSettings, initialFormData, initialTheme, initialRules, initialBookingSettings]);
  
  const loadCalendarConnections = async () => {
    setLoadingConnections(true);
    try {
      // Use per-chatbot calendar endpoint
      const response = await api.get(`/calendar/chatbot/${chatbotId}/connections`);
      setCalendarConnections(response.data.connections || []);
    } catch (error) {
      console.error('Error loading calendar connections:', error);
      // Fallback to user-level connections
      try {
        const fallback = await api.get('/calendar/connections');
        setCalendarConnections(fallback.data.connections || fallback.data || []);
      } catch (e) {
        console.error('Fallback also failed:', e);
      }
    } finally {
      setLoadingConnections(false);
    }
  };
  
  const connectGoogleCalendar = async () => {
    try {
      const response = await api.get(`/calendar/chatbot/${chatbotId}/google/connect`);
      if (response.data.authorization_url) {
        window.location.href = response.data.authorization_url;
      }
    } catch (error) {
      const detail = error.response?.data?.detail || '';
      if (detail.includes('not configured')) {
        toast.error('Google Calendar requires setup. Go to Admin > Settings to configure Google OAuth credentials.');
      } else {
        toast.error(detail || 'Failed to start Google Calendar connection');
      }
    }
  };

  const disconnectCalendar = async (connectionId) => {
    try {
      await api.delete(`/calendar/chatbot/${chatbotId}/connection/${connectionId}`);
      toast.success('Calendar disconnected');
      loadCalendarConnections();
    } catch (error) {
      toast.error('Failed to disconnect calendar');
    }
  };

  const handleSyncFromCalendar = async () => {
    setSyncingCalendar(true);
    try {
      const res = await calendarAPI.getSmartSchedule(chatbotId);
      const schedule = res.data;
      setBookingSettings(prev => ({
        ...prev,
        working_hours_start: schedule.working_hours_start,
        working_hours_end:   schedule.working_hours_end,
        working_days:        schedule.working_days,
        timezone:            schedule.timezone,
      }));
      setVacationBlocks(schedule.vacation_blocks || []);
      toast.success(`Schedule synced from Google Calendar (${schedule.calendar_email})`);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Could not sync from calendar. Make sure Google Calendar is connected.';
      toast.error(msg);
    } finally {
      setSyncingCalendar(false);
    }
  };
  
  const loadModerators = async () => {
    setLoadingModerators(true);
    try {
      const response = await moderatorsAPI.list(chatbotId);
      setModerators(response.data);
    } catch (error) {
      console.error('Error loading moderators:', error);
    } finally {
      setLoadingModerators(false);
    }
  };
  
  const handleAddModerator = async () => {
    if (!newModeratorEmail.trim()) {
      toast.error('Please enter an email address');
      return;
    }
    
    setAddingModerator(true);
    try {
      const response = await moderatorsAPI.add(chatbotId, newModeratorEmail.trim());
      toast.success(response.data.message);
      setShowAddModeratorDialog(false);
      setNewModeratorEmail('');
      loadModerators();
    } catch (error) {
      console.error('Error adding moderator:', error);
      toast.error(error.response?.data?.detail || 'Failed to add moderator');
    } finally {
      setAddingModerator(false);
    }
  };
  
  const handleRemoveModerator = async (moderatorId) => {
    try {
      await moderatorsAPI.remove(chatbotId, moderatorId);
      toast.success('Moderator removed');
      loadModerators();
    } catch (error) {
      console.error('Error removing moderator:', error);
      toast.error('Failed to remove moderator');
    }
  };

  const handleToggleModerator = async (moderatorId, currentStatus) => {
    const next = currentStatus !== 'active';
    try {
      await api.patch(`/chatbots/${chatbotId}/moderators/${moderatorId}`, { active: next });
      toast.success(next ? 'Moderator activated' : 'Moderator suspended');
      loadModerators();
    } catch (error) {
      const msg = error?.response?.data?.detail || 'Failed to update moderator';
      toast.error(typeof msg === 'string' ? msg : 'Failed to update moderator');
    }
  };

  const handleResendModeratorInvite = async (moderatorId) => {
    try {
      await api.post(`/chatbots/${chatbotId}/moderators/${moderatorId}/resend-invite`);
      toast.success('Invitation email resent');
      loadModerators();
    } catch (error) {
      const msg = error?.response?.data?.detail || 'Failed to resend invitation';
      toast.error(typeof msg === 'string' ? msg : 'Failed to resend invitation');
    }
  };
  
  const loadChannels = async () => {
    try {
      const response = await api.get(`/chatbots/${chatbotId}/channels`);
      if (response.data) {
        setChannels(prev => ({
          ...prev,
          ...response.data
        }));
      }
    } catch (error) {
      // Channels might not exist yet, that's okay
      if (process.env.NODE_ENV !== 'production') logger.log('No channels configured yet');
    }
  };
  
  const saveChannel = async (channelType, data) => {
    setSavingChannel(channelType);
    try {
      await api.post(`/chatbots/${chatbotId}/channels/${channelType}`, data);
      setChannels(prev => ({
        ...prev,
        [channelType]: { ...prev[channelType], ...data, connected: true }
      }));
      toast.success(`${channelType.charAt(0).toUpperCase() + channelType.slice(1)} connected successfully`);
    } catch (error) {
      toast.error(`Failed to connect ${channelType}`);
    } finally {
      setSavingChannel(null);
    }
  };
  
  const disconnectChannel = async (channelType) => {
    try {
      await api.delete(`/chatbots/${chatbotId}/channels/${channelType}`);
      setChannels(prev => ({
        ...prev,
        [channelType]: { connected: false }
      }));
      toast.success(`${channelType.charAt(0).toUpperCase() + channelType.slice(1)} disconnected`);
    } catch (error) {
      toast.error(`Failed to disconnect ${channelType}`);
    }
  };
  
  
  const loadChatbot = async () => {
    try {
      const response = await chatbotsAPI.listAll();
      const found = response.data.find(c => c.chatbot_id === chatbotId);
      if (found) {
        setChatbot(found);
        initializeForm(found);
        loadKnowledgeBase(found);
        loadModerators();
        loadChannels();
      } else {
        toast.error('AI Employee not found');
        navigate('/dashboard/chatbots');
      }
    } catch (error) {
      console.error('Error loading chatbot:', error);
      toast.error('Failed to load AI Employee');
    } finally {
      setLoading(false);
    }
  };

  const loadKnowledgeBase = async (bot = chatbot) => {
    if (!bot) return;
    try {
      const [docsRes, apisRes] = await Promise.all([
        knowledgeAPI.listDocuments(bot.account_id, bot.sub_account_id, bot.chatbot_id),
        knowledgeAPI.listAPISources(bot.account_id, bot.sub_account_id, bot.chatbot_id)
      ]);
      setDocuments(docsRes.data);
      setApiSources(apisRes.data);
    } catch (error) {
      console.error('Error loading knowledge base:', error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updateData = {
        ...formData,
        widget_theme: theme,
        rules: rules,
        booking_settings: bookingSettings
      };
      const response = await chatbotsAPI.update(
        chatbot.account_id,
        chatbot.sub_account_id,
        chatbot.chatbot_id,
        updateData
      );
      setChatbot(response.data);
      // Reset all initial states to current values after successful save
      setInitialFormData(JSON.stringify(formData));
      setInitialTheme(JSON.stringify(theme));
      setInitialRules(JSON.stringify(rules));
      setInitialBookingSettings(JSON.stringify(bookingSettings));
      setHasUnsavedChanges(false);
      toast.success('AI Employee updated successfully');
    } catch (error) {
      console.error('Error saving AI Employee:', error);
      toast.error('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  // Rules handlers
  const handleAddRule = () => {
    if (!newRuleText.trim()) {
      toast.error('Please enter a rule');
      return;
    }
    const newRule = {
      rule_id: `rule_${Date.now().toString(36)}`,
      content: newRuleText.trim(),
      is_active: true,
      priority: rules.length,
      created_at: new Date().toISOString()
    };
    setRules([...rules, newRule]);
    setNewRuleText('');
    toast.success('Rule added - remember to save changes');
  };

  const handleUpdateRule = (ruleId, newContent) => {
    setRules(rules.map(r => 
      r.rule_id === ruleId ? { ...r, content: newContent } : r
    ));
    setEditingRule(null);
    toast.success('Rule updated - remember to save changes');
  };

  const handleToggleRule = (ruleId) => {
    setRules(rules.map(r => 
      r.rule_id === ruleId ? { ...r, is_active: !r.is_active } : r
    ));
  };

  const handleDeleteRule = (ruleId) => {
    setRules(rules.filter(r => r.rule_id !== ruleId));
    toast.success('Rule removed - remember to save changes');
  };

  const handleMoveRule = (ruleId, direction) => {
    const index = rules.findIndex(r => r.rule_id === ruleId);
    if ((direction === 'up' && index === 0) || (direction === 'down' && index === rules.length - 1)) return;
    
    const newRules = [...rules];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    [newRules[index], newRules[newIndex]] = [newRules[newIndex], newRules[index]];
    // Update priorities
    newRules.forEach((r, i) => r.priority = newRules.length - i);
    setRules(newRules);
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      await knowledgeAPI.uploadDocument(
        chatbot.account_id,
        chatbot.sub_account_id,
        chatbot.chatbot_id,
        file
      );
      toast.success('Document uploaded successfully');
      loadKnowledgeBase();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  const handleAddTextDoc = async () => {
    if (!newTextDoc.name.trim() || !newTextDoc.content.trim()) {
      toast.error('Please fill in all fields');
      return;
    }
    try {
      await knowledgeAPI.addTextDocument(
        chatbot.account_id,
        chatbot.sub_account_id,
        chatbot.chatbot_id,
        newTextDoc
      );
      toast.success('Document added successfully');
      setShowTextDocDialog(false);
      setNewTextDoc({ name: '', content: '' });
      loadKnowledgeBase();
    } catch (error) {
      toast.error('Failed to add document');
    }
  };

  // Direct delete (called by the confirm dialog). The KnowledgeTab trash
  // icon and ViewDocumentDialog Delete button now populate `docToDelete`
  // and open DeleteConfirmDialog instead of calling this directly, so
  // we never wipe a document on a mis-click.
  const handleDeleteDocument = async (documentId) => {
    try {
      await knowledgeAPI.deleteDocument(
        chatbot.account_id,
        chatbot.sub_account_id,
        chatbot.chatbot_id,
        documentId
      );
      toast.success('Document deleted');
      loadKnowledgeBase();
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const requestDeleteDocument = (doc) => setDocToDelete(doc);

  const confirmDeleteDocument = async () => {
    if (!docToDelete) return;
    setDeletingDoc(true);
    try {
      await handleDeleteDocument(docToDelete.document_id);
    } finally {
      setDeletingDoc(false);
      setDocToDelete(null);
    }
  };

  const handleAddAPISource = async () => {
    if (!newAPISource.name.trim() || !newAPISource.endpoint_url.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }
    try {
      await knowledgeAPI.createAPISource(
        chatbot.account_id,
        chatbot.sub_account_id,
        chatbot.chatbot_id,
        newAPISource
      );
      toast.success('API source added successfully');
      setShowAPISourceDialog(false);
      setNewAPISource({ name: '', endpoint_url: '', method: 'GET', refresh_interval_minutes: 60 });
      loadKnowledgeBase();
    } catch (error) {
      toast.error('Failed to add API source');
    }
  };

  // Test chat functions
  const sendTestMessage = async () => {
    if (!testInput.trim() || testLoading) return;
    
    const userMsg = testInput.trim();
    setTestInput('');
    setTestMessages(prev => [...prev, { role: 'user', content: userMsg }]);
    setTestLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/chat/${chatbot.chatbot_id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': chatbot.api_key
        },
        body: JSON.stringify({
          message: userMsg,
          session_id: testSessionId
        })
      });

      if (response.ok) {
        const data = await response.json();
        setTestSessionId(data.session_id);
        setTestMessages(prev => [...prev, { 
          role: 'assistant', 
          content: data.message,
          sources: data.sources 
        }]);
      } else {
        const error = await response.json();
        setTestMessages(prev => [...prev, { 
          role: 'assistant', 
          content: `Error: ${error.detail || 'Failed to get response'}` 
        }]);
      }
    } catch (error) {
      setTestMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Error: Could not connect to the AI agent' 
      }]);
    } finally {
      setTestLoading(false);
    }
  };

  const resetTestChat = () => {
    setTestMessages([]);
    setTestSessionId(null);
    if (chatbot?.welcome_message) {
      setTestMessages([{ role: 'welcome', content: chatbot.welcome_message }]);
    }
  };

  const openTestChat = () => {
    setTestChatOpen(true);
    resetTestChat();
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Could not copy to clipboard');
    }
  };

  // URL document handler
  const handleAddUrl = async () => {
    if (!urlInput.trim()) {
      toast.error('Please enter a URL');
      return;
    }
    
    // Validate URL format
    try {
      new URL(urlInput);
    } catch {
      toast.error('Please enter a valid URL');
      return;
    }
    
    setAddingUrl(true);
    try {
      await knowledgeAPI.addTextDocument(
        chatbot.account_id,
        chatbot.sub_account_id,
        chatbot.chatbot_id,
        {
          name: urlName || urlInput,
          document_type: 'url',
          content: urlInput,
          source_type: 'url',
          source_url: urlInput
        }
      );
      toast.success('URL added to knowledge base');
      setShowUrlDialog(false);
      setUrlInput('');
      setUrlName('');
      loadKnowledgeBase();
    } catch (error) {
      console.error('Error adding URL:', error);
      toast.error(error.response?.data?.detail || 'Failed to add URL');
    } finally {
      setAddingUrl(false);
    }
  };

  // Sitemap import handler
  const handleImportSitemap = async () => {
    if (!sitemapUrl.trim()) {
      toast.error('Please enter a sitemap URL');
      return;
    }
    
    // Validate max pages
    const pages = parseInt(maxPages);
    if (isNaN(pages) || pages < 1 || pages > 50) {
      toast.error('Please enter a valid number between 1 and 50 for maximum pages');
      return;
    }
    
    setImportingSitemap(true);
    try {
      const response = await api.post(
        `/accounts/${chatbot.account_id}/sub-accounts/${chatbot.sub_account_id}/chatbots/${chatbot.chatbot_id}/import-sitemap`,
        { sitemap_url: sitemapUrl, max_pages: pages, deep_crawl: deepCrawl }
      );
      toast.success(`Importing ${response.data.import_started} pages from sitemap`);
      setShowSitemapDialog(false);
      setSitemapUrl('');
      setMaxPages(20);
      // Refresh documents list after a delay
      setTimeout(loadKnowledgeBase, 2000);
    } catch (error) {
      console.error('Error importing sitemap:', error);
      toast.error(error.response?.data?.detail || 'Failed to import sitemap');
    } finally {
      setImportingSitemap(false);
    }
  };

  // View document handler - shows content in modal (safer than popup)
  const handleViewDocument = async (doc) => {
    // For URL-sourced documents, open the source URL directly
    if (doc.source_url) {
      window.open(doc.source_url, '_blank');
      return;
    }
    
    // For PDFs (and any file type where the original bytes are preserved
    // on disk) we render the original in an iframe so the user gets a
    // proper page-by-page preview instead of the text-extracted <pre>.
    const looksLikePdf = (doc.name || '').toLowerCase().endsWith('.pdf');
    if (looksLikePdf) {
      const backend = process.env.REACT_APP_BACKEND_URL || '';
      const previewUrl = `${backend}/api/accounts/${chatbot.account_id}/sub-accounts/${chatbot.sub_account_id}/chatbots/${chatbot.chatbot_id}/documents/${doc.document_id}/preview`;
      setViewingDocument({ ...doc, previewUrl });
      setShowDocViewDialog(true);
      return;
    }

    // Try to fetch full document content if not available
    let content = doc.content;
    if (!content) {
      try {
        toast.loading('Loading document...', { id: 'doc-loading' });
        const response = await knowledgeAPI.getDocument(
          chatbot.account_id,
          chatbot.sub_account_id,
          chatbot.chatbot_id,
          doc.document_id,
          true // include content
        );
        content = response.data.content;
        toast.dismiss('doc-loading');
      } catch (error) {
        console.error('Error fetching document content:', error);
        toast.error('Failed to load document content');
        return;
      }
    }
    
    // Show in modal dialog (more reliable than popup which can be blocked)
    if (content) {
      setViewingDocument({ ...doc, content });
      setShowDocViewDialog(true);
    } else {
      toast.error('No content available for this document');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-emerald-100 text-emerald-700 border-emerald-200';
      case 'draft': return 'bg-slate-100 text-slate-600 border-slate-200';
      case 'paused': return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'ready': return 'bg-emerald-100 text-emerald-700 border-emerald-200';
      case 'pending': return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'processing': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'failed': return 'bg-red-100 text-red-700 border-red-200';
      default: return 'bg-slate-100 text-slate-600 border-slate-200';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
      </div>
    );
  }

  if (!chatbot) {
    return <div className="text-center py-12 text-slate-500">AI Employee not found</div>;
  }

  // Use production domain for embed code (users will deploy this on their own sites)
  const PRODUCTION_DOMAIN = "https://harmonix.info";
  // Widget position: 'left' or 'right' from theme settings
  const widgetPosition = theme.position === 'bottom-left' ? 'left' : 'right';
  const embedCode = `<script src="${PRODUCTION_DOMAIN}/widget.js" data-chatbot-id="${chatbot.chatbot_id}" data-api-key="${chatbot.api_key}" data-position="${widgetPosition}"></script>`;
  
  // Alternative secure embed code (token-based, hides AI Employee ID)
  const secureEmbedCode = `<script src="${PRODUCTION_DOMAIN}/widget.js" data-token="${chatbot.embed_token || chatbot.chatbot_id}" data-position="${widgetPosition}"></script>`;

  // Predefined persona tags for quick chatbot setup (shared with the wizard so labels stay in sync).
  const personaTags = PERSONA_TAGS;

  const applyPersonaTag = (tag) => {
    setFormData(prev => ({ ...prev, system_prompt: tag.prompt }));
    toast.success(`Applied "${tag.name}" persona`);
  };

  // Toggle chatbot status
  const toggleStatus = async () => {
    const newStatus = formData.status === 'active' ? 'draft' : 'active';
    setFormData(prev => ({ ...prev, status: newStatus }));
  };

  return (
    <div className="space-y-6" data-testid="chatbot-detail-page">
      {/* Header with Status Toggle */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        {/* Back button and title */}
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard/chatbots')} className="text-slate-500 hover:text-slate-900 flex-shrink-0">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center flex-shrink-0">
            <Bot className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
          </div>
          <div className="min-w-0">
            <h1 className="text-lg sm:text-2xl font-bold text-slate-900 truncate">{chatbot.name}</h1>
          </div>
        </div>
        
        {/* Status and Actions Row */}
        <div className="flex flex-wrap items-center gap-2 sm:gap-3 justify-end">
          {/* Status Toggle */}
          <div className="flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-2 border border-slate-200">
            <span className="text-xs sm:text-sm text-slate-600 hidden xs:inline">Status:</span>
            <Switch
              checked={formData.status === 'active'}
              onCheckedChange={toggleStatus}
              className="data-[state=checked]:bg-emerald-500"
            />
            <Badge className={`text-xs ${formData.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
              {formData.status === 'active' ? 'Active' : 'Draft'}
            </Badge>
          </div>
          
          {/* Action Buttons */}
          <div className="flex items-center gap-2">
            <HelpButton 
              title="AI Employee Configuration"
              content={`
                <p>Configure and manage your AI agent from this page.</p>
                <p class="mt-2"><strong>Tabs:</strong></p>
                <ul>
                  <li><strong>Settings:</strong> Configure name, system prompt, and status</li>
                  <li><strong>Knowledge:</strong> Upload documents and add API sources</li>
                  <li><strong>Theme:</strong> Customize widget appearance</li>
                  <li><strong>Embed:</strong> Get the code to add the agent to your site</li>
                </ul>
                <p class="mt-2"><strong>Note:</strong> Updating settings does NOT require re-adding the embed code. Changes apply automatically.</p>
              `}
            />
            <Button onClick={openTestChat} variant="outline" size="sm" className="border-slate-300 text-slate-700" data-testid="test-chat-btn">
              <MessageSquare className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Test</span>
            </Button>
            <Button 
              onClick={handleSave} 
              disabled={saving || !hasUnsavedChanges}
              size="sm"
              className={hasUnsavedChanges 
                ? 'bg-emerald-600 hover:bg-emerald-700' 
                : 'bg-slate-300 text-slate-500 cursor-not-allowed'
              }
              data-testid="save-chatbot-btn"
            >
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              <span className="hidden sm:inline">{hasUnsavedChanges ? 'Update' : 'Saved'}</span>
              <span className="sm:hidden">{hasUnsavedChanges ? '💾' : '✓'}</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue={defaultTab} className="space-y-6">
        <TabsList className="bg-slate-100 border border-slate-200 p-1 flex flex-wrap gap-1 h-auto">
          <TabsTrigger value="settings" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white text-xs sm:text-sm" data-testid="agent-settings-tab">
            <Settings className="w-4 h-4 sm:mr-2" /><span className="hidden sm:inline">Agent Settings</span>
          </TabsTrigger>
          <TabsTrigger value="rules" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white text-xs sm:text-sm" data-testid="agent-rules-tab">
            <FileText className="w-4 h-4 sm:mr-2" /><span className="hidden sm:inline">Rules</span>
          </TabsTrigger>
          <TabsTrigger value="knowledge" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white text-xs sm:text-sm" data-testid="agent-knowledge-tab">
            <Database className="w-4 h-4 sm:mr-2" /><span className="hidden sm:inline">Knowledge</span>
          </TabsTrigger>
          <TabsTrigger value="channels" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white text-xs sm:text-sm" data-testid="agent-channels-tab">
            <Share2 className="w-4 h-4 sm:mr-2" /><span className="hidden sm:inline">Channels</span>
          </TabsTrigger>
          <TabsTrigger value="interactions" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white text-xs sm:text-sm" data-testid="agent-interactions-tab">
            <MessageSquare className="w-4 h-4 sm:mr-2" /><span className="hidden sm:inline">Interactions</span>
          </TabsTrigger>
          {!isModeratorOnly && (
          <TabsTrigger value="moderators" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white text-xs sm:text-sm" data-testid="agent-moderators-tab">
            <Users className="w-4 h-4 sm:mr-2" /><span className="hidden sm:inline">Moderators</span>
          </TabsTrigger>
          )}
          <TabsTrigger value="theme" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white text-xs sm:text-sm" data-testid="agent-theme-tab">
            <Palette className="w-4 h-4 sm:mr-2" /><span className="hidden sm:inline">Theme</span>
          </TabsTrigger>
          <TabsTrigger value="embed" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white text-xs sm:text-sm" data-testid="agent-embed-tab">
            <Code className="w-4 h-4 sm:mr-2" /><span className="hidden sm:inline">Embed</span>
          </TabsTrigger>
          <TabsTrigger value="support-portals" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white text-xs sm:text-sm" data-testid="agent-support-portals-tab">
            <Headphones className="w-4 h-4 sm:mr-2" /><span className="hidden sm:inline">Help Desk</span>
          </TabsTrigger>
          <TabsTrigger
            value="automations"
            className="data-[state=active]:bg-blue-600 data-[state=active]:text-white text-xs sm:text-sm"
            onClick={() => navigate(`/dashboard/automations?chatbot_id=${chatbotId}`)}
            data-testid="agent-automations-tab"
          >
            <Zap className="w-4 h-4 sm:mr-2" /><span className="hidden sm:inline">Automations</span>
          </TabsTrigger>
          {!isModeratorOnly && (
          <TabsTrigger value="history" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white text-xs sm:text-sm" data-testid="agent-history-tab">
            <History className="w-4 h-4 sm:mr-2" /><span className="hidden sm:inline">History</span>
          </TabsTrigger>
          )}
        </TabsList>

        {/* Bot Settings Tab */}
        <TabsContent value="settings" className="space-y-6">
          <SettingsTab
            formData={formData}
            setFormData={setFormData}
            personaTags={personaTags}
            applyPersonaTag={applyPersonaTag}
            setHasUnsavedChanges={setHasUnsavedChanges}
            bookingSettings={bookingSettings}
            setBookingSettings={setBookingSettings}
            calendarConnections={calendarConnections}
            loadingConnections={loadingConnections}
            connectGoogleCalendar={connectGoogleCalendar}
            disconnectCalendar={disconnectCalendar}
            handleSyncFromCalendar={handleSyncFromCalendar}
            syncingCalendar={syncingCalendar}
            vacationBlocks={vacationBlocks}
          />
        </TabsContent>

        {/* Rules Tab */}
        <TabsContent value="rules" className="space-y-6">
          <RulesTab
            rules={rules}
            newRuleText={newRuleText}
            setNewRuleText={setNewRuleText}
            editingRule={editingRule}
            setEditingRule={setEditingRule}
            handleAddRule={handleAddRule}
            handleUpdateRule={handleUpdateRule}
            handleToggleRule={handleToggleRule}
            handleDeleteRule={handleDeleteRule}
            handleMoveRule={handleMoveRule}
          />
        </TabsContent>

        {/* Knowledge Base Tab */}
        <TabsContent value="knowledge" className="space-y-6">
          <KnowledgeTab
            documents={documents}
            apiSources={apiSources}
            uploading={uploading}
            refreshingKnowledge={refreshingKnowledge}
            setRefreshingKnowledge={setRefreshingKnowledge}
            loadKnowledgeBase={loadKnowledgeBase}
            handleFileUpload={handleFileUpload}
            handleViewDocument={handleViewDocument}
            handleDeleteDocument={requestDeleteDocument}
            setShowSitemapDialog={setShowSitemapDialog}
            setShowTextDocDialog={setShowTextDocDialog}
            setShowAPISourceDialog={setShowAPISourceDialog}
            getStatusColor={getStatusColor}
          />
        </TabsContent>

        {/* Channels Tab - Social Media Integrations */}
        <TabsContent value="channels" className="space-y-6">
          <ChannelsTab
            chatbotId={chatbotId}
            chatbot={chatbot}
            API_URL={API_URL}
            socialChannelsEnabled={socialChannelsEnabled}
            featureAccess={featureAccess}
            channels={channels}
            setChannels={setChannels}
            savingChannel={savingChannel}
            setSavingChannel={setSavingChannel}
            loadChannels={loadChannels}
            saveChannel={saveChannel}
            disconnectChannel={disconnectChannel}
          />
        </TabsContent>

        {/* Interactions Tab - Unified inbox across all channels */}
        <TabsContent value="interactions" className="space-y-6">
          <InteractionsTab chatbotId={chatbotId} chatbot={chatbot} />
        </TabsContent>

        {/* Moderators Tab */}
        <TabsContent value="moderators" className="space-y-6">
          <ModeratorsTab
            moderators={moderators}
            loadingModerators={loadingModerators}
            setShowAddModeratorDialog={setShowAddModeratorDialog}
            handleResendModeratorInvite={handleResendModeratorInvite}
            handleToggleModerator={handleToggleModerator}
            handleRemoveModerator={handleRemoveModerator}
          />
        </TabsContent>

        {/* Theme Tab */}
        <TabsContent value="theme" className="space-y-6">
          <ThemeTab theme={theme} setTheme={setTheme} formData={formData} chatbotId={chatbotId} />
        </TabsContent>

        {/* Embed Tab */}
        <TabsContent value="embed" className="space-y-6">
          <EmbedTab
            chatbot={chatbot}
            formData={formData}
            theme={theme}
            embedCode={embedCode}
            copied={copied}
            copyToClipboard={copyToClipboard}
          />
        </TabsContent>

        {/* Support Portals Tab (Zendesk / Freshdesk / Jira — read-only + internal notes) */}
        <TabsContent value="support-portals" className="space-y-6">
          <SupportPortalsTab chatbotId={chatbotId} />
        </TabsContent>

        {!isModeratorOnly && (
        <TabsContent value="history" className="space-y-6">
          <AgentHistoryTab chatbotId={chatbotId} />
        </TabsContent>
        )}
      </Tabs>

      <AddTextDocumentDialog
        open={showTextDocDialog}
        onOpenChange={setShowTextDocDialog}
        newTextDoc={newTextDoc}
        setNewTextDoc={setNewTextDoc}
        onSubmit={handleAddTextDoc}
      />

      <AddAPISourceDialog
        open={showAPISourceDialog}
        onOpenChange={setShowAPISourceDialog}
        newAPISource={newAPISource}
        setNewAPISource={setNewAPISource}
        onSubmit={handleAddAPISource}
        onOpenHelp={() => { setShowAPISourceDialog(false); setShowAPISourceHelp(true); }}
      />

      {/* API Source Help Modal — full walkthrough */}
      <Dialog open={showAPISourceHelp} onOpenChange={setShowAPISourceHelp}>
        <DialogContent className="bg-white border-slate-200 max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-slate-900">API Data Source — Walkthrough</DialogTitle>
            <p className="text-sm text-slate-500 mt-1">Step-by-step example connecting a product-catalog API to your AI Employee.</p>
          </DialogHeader>
          <div className="space-y-5 py-4 text-sm">
            <section>
              <h4 className="font-semibold text-slate-900 mb-1">1. Find your API endpoint</h4>
              <p className="text-slate-600">Any REST API that returns JSON works. Common pickings:</p>
              <ul className="list-disc list-inside text-slate-600 mt-1 space-y-0.5">
                <li>Shopify Admin API <code className="bg-slate-100 px-1 rounded text-xs">GET /admin/api/products.json</code></li>
                <li>WooCommerce <code className="bg-slate-100 px-1 rounded text-xs">GET /wp-json/wc/v3/products</code></li>
                <li>Your own backend, e.g. <code className="bg-slate-100 px-1 rounded text-xs">GET https://api.acme.com/v1/menu</code></li>
              </ul>
            </section>
            <section>
              <h4 className="font-semibold text-slate-900 mb-1">2. Test it with curl</h4>
              <pre className="bg-slate-900 text-emerald-300 rounded-lg p-3 text-xs font-mono overflow-x-auto">{`curl -H "Authorization: Bearer YOUR_API_KEY" \\
     https://api.acme.com/v1/menu`}</pre>
              <p className="text-xs text-slate-500 mt-1">If the response is JSON and ≤ 1 MB, you&apos;re good.</p>
            </section>
            <section>
              <h4 className="font-semibold text-slate-900 mb-1">3. Expected JSON shape</h4>
              <pre className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs font-mono overflow-x-auto text-slate-700">{`[
  {
    "id": "burger-classic",
    "name": "Classic Burger",
    "price": 8.99,
    "vegetarian": false,
    "stock": 42
  },
  ...
]`}</pre>
              <p className="text-xs text-slate-500 mt-1">Field names become askable: “Is the Classic Burger vegetarian?” will resolve to the matching record.</p>
            </section>
            <section>
              <h4 className="font-semibold text-slate-900 mb-1">4. Real-world use cases</h4>
              <div className="grid sm:grid-cols-2 gap-3 mt-2">
                {[
                  { title: 'E-commerce', desc: 'Live inventory & pricing — “Do you have item X in stock at the Brooklyn store?”' },
                  { title: 'Restaurants', desc: 'Menu & availability — “What vegan dishes do you have under $10?”' },
                  { title: 'Clinics & Salons', desc: 'Free slots from your scheduling API — “Any openings Saturday morning?”' },
                  { title: 'SaaS Support', desc: 'Fetch the user&apos;s plan + usage from your billing API — “How many seats are left on my plan?”' },
                ].map((u) => (
                  <div key={u.title} className="border border-slate-200 rounded-lg p-3 bg-slate-50">
                    <p className="font-medium text-slate-900 text-sm">{u.title}</p>
                    <p className="text-xs text-slate-600 mt-1">{u.desc}</p>
                  </div>
                ))}
              </div>
            </section>
            <section className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
              <strong>Heads up:</strong> if your API needs an API key, save the source first then add a header from the source&apos;s edit panel (<em>Headers</em> → <code>Authorization: Bearer …</code>). Never paste secrets into the URL.
            </section>
          </div>
          <DialogFooter>
            <Button onClick={() => setShowAPISourceHelp(false)} className="bg-blue-600 hover:bg-blue-700">Got it</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Test Chat Dialog */}
      <Dialog open={testChatOpen} onOpenChange={setTestChatOpen}>
        <DialogContent className="bg-slate-50 border-none shadow-2xl max-w-md h-[650px] flex flex-col p-0 rounded-2xl overflow-hidden [&>button.absolute]:hidden">
          {/* Header */}
          <div 
            className="px-5 py-4 flex items-center justify-between shadow-md"
            style={{ background: `linear-gradient(135deg, ${theme.primary_color} 0%, ${theme.secondary_color} 100%)` }}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center overflow-hidden">
                {formData.avatar_url ? (
                  <img src={formData.avatar_url} alt={formData.name || 'AI'} className="w-full h-full object-cover" data-testid="test-chat-avatar" />
                ) : (
                  <Bot className="w-5 h-5 text-white" />
                )}
              </div>
              <div>
                <h3 className="text-white font-semibold text-lg">{formData.name || 'Test Chat'}</h3>
                <p className="text-white/70 text-xs">AI Assistant</p>
              </div>
            </div>
            <Button variant="ghost" size="icon" onClick={resetTestChat} className="text-white/80 hover:text-white hover:bg-white/20 rounded-full">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
          
          {/* Messages Area */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gradient-to-b from-slate-50 to-white">
            {testMessages.map((msg, i) => {
              // Treat 'welcome' role the same as 'assistant' visually — show
              // the bot avatar and a proper bubble container. Previously the
              // greeting rendered as bare centered italic text with no avatar,
              // which looked like a system notice rather than the bot speaking.
              const isUser = msg.role === 'user';
              const showBotAvatar = !isUser;
              return (
              <div key={msg.id || msg.timestamp || `msg-${i}-${(msg.content || '').slice(0, 12)}`} className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
                {showBotAvatar && (
                  <div className="w-8 h-8 rounded-full mr-2 flex-shrink-0 flex items-center justify-center shadow-sm overflow-hidden"
                    style={{ background: `linear-gradient(135deg, ${theme.primary_color} 0%, ${theme.secondary_color} 100%)` }}>
                    {formData.avatar_url ? (
                      <img src={formData.avatar_url} alt={formData.name || 'AI'} className="w-full h-full object-cover" />
                    ) : (
                      <Bot className="w-4 h-4 text-white" />
                    )}
                  </div>
                )}
                <div className={`max-w-[80%] ${
                  isUser
                    ? 'bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-2xl rounded-br-md px-4 py-3 shadow-md'
                    : 'bg-white text-slate-700 rounded-2xl rounded-bl-md px-4 py-3 shadow-sm border border-slate-100'
                }`}>
                  {msg.role === 'user' ? (
                    <p className="text-sm leading-relaxed">{msg.content}</p>
                  ) : (
                    // Render Markdown so headings, lists, bold, code, and
                    // links show up formatted instead of as raw
                    // `**text**` and `- bullet` characters. Restrict image
                    // tags implicitly (react-markdown escapes raw HTML by
                    // default).
                    <div className="text-sm leading-relaxed prose prose-sm max-w-none prose-slate prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-headings:mt-2 prose-headings:mb-1">
                      <ReactMarkdown
                        components={{
                          a: (props) => (
                            <a {...props} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline" />
                          ),
                        }}
                      >
                        {msg.content || ''}
                      </ReactMarkdown>
                    </div>
                  )}
                  {msg.sources?.length > 0 && (() => {
                    // De-duplicate sources by document_name so repeated chunks
                    // from the same file appear once. Sources without a name
                    // (e.g., legacy content_snippet-only rows) are ignored.
                    const seen = new Set();
                    const uniqSources = [];
                    for (const s of msg.sources) {
                      const key = (s.document_name || '').toLowerCase().trim();
                      if (!key || seen.has(key)) continue;
                      seen.add(key);
                      uniqSources.push(s);
                    }
                    if (!uniqSources.length) return null;
                    return (
                    <div className="mt-3 pt-3 border-t border-slate-200/50">
                      <p className="text-xs text-slate-400 mb-2">Sources:</p>
                      <div className="flex flex-wrap gap-1">
                        {uniqSources.map((s) => (
                          <span key={s.document_id || s.document_name} className="inline-flex items-center gap-1 bg-blue-50 text-blue-600 text-xs px-2 py-1 rounded-full">
                            <FileText className="w-3 h-3" />
                            {s.document_name}
                          </span>
                        ))}
                      </div>
                    </div>
                    );
                  })()}
                </div>
                {isUser && (
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-slate-600 to-slate-800 ml-2 flex-shrink-0 flex items-center justify-center shadow-sm">
                    <User className="w-4 h-4 text-white" />
                  </div>
                )}
              </div>
              );
            })}
            {testLoading && (
              <div className="flex justify-start">
                <div className="w-8 h-8 rounded-full mr-2 flex-shrink-0 flex items-center justify-center shadow-sm overflow-hidden"
                  style={{ background: `linear-gradient(135deg, ${theme.primary_color} 0%, ${theme.secondary_color} 100%)` }}>
                  {formData.avatar_url ? (
                    <img src={formData.avatar_url} alt={formData.name || 'AI'} className="w-full h-full object-cover" />
                  ) : (
                    <Bot className="w-4 h-4 text-white" />
                  )}
                </div>
                <div className="bg-white p-4 rounded-2xl rounded-bl-md flex gap-1.5 shadow-sm border border-slate-100">
                  <span className="w-2.5 h-2.5 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                  <span className="w-2.5 h-2.5 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                  <span className="w-2.5 h-2.5 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
          
          {/* Input Area */}
          <div className="p-4 bg-white border-t border-slate-100 shadow-inner">
            {chatbot.status !== 'active' ? (
              <div className="flex items-center justify-center gap-2 py-2 px-4 bg-amber-50 rounded-xl border border-amber-200">
                <AlertCircle className="w-4 h-4 text-amber-500" />
                <p className="text-sm text-amber-700">Set AI agent status to "Active" to test</p>
              </div>
            ) : (
              <div className="flex gap-2">
                <Input 
                  value={testInput} 
                  onChange={(e) => setTestInput(e.target.value)} 
                  onKeyPress={(e) => e.key === 'Enter' && !testLoading && sendTestMessage()}
                  placeholder="Type a message..." 
                  className="bg-slate-50 border-slate-200 text-slate-900 placeholder:text-slate-400 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={testLoading}
                />
                <Button 
                  onClick={sendTestMessage} 
                  disabled={testLoading || !testInput.trim()} 
                  className="bg-gradient-to-br from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 rounded-xl px-4 shadow-md transition-all hover:shadow-lg"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <ViewDocumentDialog
        open={showDocViewDialog}
        onOpenChange={setShowDocViewDialog}
        document={viewingDocument}
        getStatusColor={getStatusColor}
        onRequestDelete={(doc) => {
          setShowDocViewDialog(false);
          setDocToDelete(doc);
        }}
      />

      {/* iter132.2 — confirm before deleting a document */}
      <DeleteConfirmDialog
        open={!!docToDelete}
        onOpenChange={(open) => { if (!open) setDocToDelete(null); }}
        title="Delete this document?"
        description={
          docToDelete
            ? <>Are you sure you want to delete <strong>{docToDelete.name}</strong>? This will remove it from the AI Employee&apos;s knowledge base immediately. This action cannot be undone.</>
            : 'Are you sure? This action cannot be undone.'
        }
        confirmLabel="Delete document"
        confirmTone="danger"
        busy={deletingDoc}
        onConfirm={confirmDeleteDocument}
        testIdPrefix="delete-doc"
      />

      <SitemapImportDialog
        open={showSitemapDialog}
        onOpenChange={setShowSitemapDialog}
        sitemapUrl={sitemapUrl}
        setSitemapUrl={setSitemapUrl}
        maxPages={maxPages}
        setMaxPages={setMaxPages}
        deepCrawl={deepCrawl}
        setDeepCrawl={setDeepCrawl}
        importingSitemap={importingSitemap}
        onSubmit={handleImportSitemap}
      />

      <AddModeratorDialog
        open={showAddModeratorDialog}
        onOpenChange={setShowAddModeratorDialog}
        newModeratorEmail={newModeratorEmail}
        setNewModeratorEmail={setNewModeratorEmail}
        addingModerator={addingModerator}
        onSubmit={handleAddModerator}
      />

      {/* Add Custom Domain Dialog removed — custom domains feature deprecated. */}
    </div>
  );
};

export default ChatbotDetailPage;

// NOTE: LeadsTab and ChatAnalytics components have been extracted to:
// - /app/frontend/src/components/chatbot/LeadsTab.jsx
// - /app/frontend/src/components/chatbot/ChatAnalytics.jsx

// The following inline component definitions have been removed from this file:
// - LeadsTab (was ~440 lines)
// - ChatAnalytics (was ~160 lines)
// 
// Import them from '@/components/chatbot' instead.
